import { useEffect, useMemo, useState } from "react";
import { financialAccountService } from "../services/financialAccountService";
import { FinancialAccountSimple } from "../types/accounts";

// Session-lifetime cache, same shape as useAssetCategories.ts — every
// payment/expense/asset form's "Account" dropdown would otherwise trigger
// its own fetch. Unlike asset categories, accounts get created/edited
// often (this is their own page, not a rarely-touched Settings list), so
// this cache needs to be explicitly invalidated after a mutation — call
// invalidateAccountsCache() after create/update/delete.
let cache: FinancialAccountSimple[] | null = null;
let inflight: Promise<FinancialAccountSimple[]> | null = null;

function fetchAccounts(): Promise<FinancialAccountSimple[]> {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
        inflight = financialAccountService
            .getSimple()
            .then((data) => {
                cache = data;
                return data;
            })
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}

export function invalidateAccountsCache(): void {
    cache = null;
}

export function useAccounts() {
    const [accounts, setAccounts] = useState<FinancialAccountSimple[]>(cache || []);
    const [loading, setLoading] = useState(!cache);
    const [error, setError] = useState("");
    // Bumped by refetch() to force the effect below to re-run even though
    // its dependency array is otherwise empty.
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (cache && refreshToken === 0) return;
        let cancelled = false;
        setLoading(true);
        fetchAccounts()
            .then((data) => {
                if (!cancelled) setAccounts(data);
            })
            .catch(() => {
                if (!cancelled) setError("Failed to load accounts");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [refreshToken]);

    const byId = useMemo(
        () => Object.fromEntries(accounts.map((a) => [a.account_id, a])),
        [accounts],
    );

    const getAccountName = (accountId: string | undefined | null): string | undefined =>
        accountId ? byId[accountId]?.name : undefined;

    const refetch = () => {
        invalidateAccountsCache();
        setRefreshToken((t) => t + 1);
    };

    return { accounts, loading, error, getAccountName, refetch };
}
